import { Injectable } from '@angular/core';
import { Maybe } from '@orderna/admin-panel/src/types/maybe';
import { QueryOptions } from '@orderna/admin-panel/src/types/query-options';
import {
  createItem,
  Data,
  deleteAllItems,
  deleteAllItemsExcept,
  deleteItem,
  deleteItems,
  filterItems,
  updateItem,
} from '@orderna/admin-panel/src/utils/service';
import { BehaviorSubject, Observable } from 'rxjs';
import { DISCOUNTS } from '../../data/discounts';
import {
  DiscountDetail,
  DiscountSummary,
  IDiscountCreateData,
  IDiscountDetail,
  IDiscountSummary,
  IDiscountUpdateData,
} from '../../model/discount';
import { DiscountStatus } from '../../model/enum/discount-status';
import { IDiscountsApiService } from './discounts-api.interface';
import { Customer } from '../../model/customer';
import { ApplicableDiscount } from '../../model/discount-applicable';
import { Store } from '../../model/store';
import { DiscountFormData } from '../../model/discount-form-data';
import { DiscountDetails } from '../../model/discount-details';
import { FindDiscountsQueryDto } from './dtos/find-discounts.query.dto';
import { FindDiscountsResponseDto } from './dtos/find-discounts.response.dto';

@Injectable({
  providedIn: 'root',
})
export class DiscountsMockApiService implements IDiscountsApiService {
  data = {
    discounts: {
      items: [...DISCOUNTS],
      queryOptions: { page: 1, size: 10 } as QueryOptions<IDiscountDetail>,
      subject: new BehaviorSubject<Maybe<DiscountDetail[]>>(null),
      totalSubject: new BehaviorSubject<Maybe<number>>(null),
      totalAfterFilterSubject: new BehaviorSubject<Maybe<number>>(null),
    },
  };

  get discountsData() {
    return this.data['discounts'] as Data<DiscountDetail, IDiscountDetail>;
  }

  createDiscount(discount: IDiscountCreateData): Observable<any> {
    throw new Error('Method not implemented.');
  }

  updateDiscount(discount: IDiscountUpdateData): Observable<any> {
    throw new Error('Method not implemented.');
  }

  deleteDiscount(
    discountId: DiscountSummary['id']
  ): Observable<IDiscountSummary> {
    return deleteItem(this.discountsData, discountId);
  }

  deleteDiscounts(
    discountIds: DiscountSummary['id'][]
  ): Observable<IDiscountSummary[]> {
    return deleteItems(this.discountsData, discountIds);
  }

  deleteAllDiscounts(): Observable<IDiscountSummary[]> {
    return deleteAllItems(this.discountsData);
  }

  deleteAllDiscountsExcept(
    discountIds: DiscountSummary['id'][]
  ): Observable<IDiscountSummary[]> {
    return deleteAllItemsExcept(this.discountsData, discountIds);
  }

  findDiscountFormData(discountId: number): Observable<DiscountFormData> {
    throw new Error('Method not implemented.');
  }

  findDiscounts(
    query: FindDiscountsQueryDto
  ): Promise<FindDiscountsResponseDto> {
    throw new Error('Method not implemented.');
  }

  getApplicableDiscounts(
    storeId: Store['id'],
    amount: number,
    customerId?: Customer['id']
  ): Observable<ApplicableDiscount[]> {
    throw new Error('Method not implemented.');
  }

  private getDiscountStatus(validFrom: Date, validThrough: Date | null) {
    const now = new Date();

    // Check if the current date falls within the valid range
    if (now >= validFrom && (validThrough === null || now <= validThrough)) {
      return DiscountStatus.ACTIVE;
    }

    // Check if the valid range is in the future
    if (now < validFrom) {
      return DiscountStatus.SCHEDULED;
    }

    // Check if the valid range is in the past
    if (validThrough !== null && now > validThrough) {
      return DiscountStatus.EXPIRED;
    }

    // If validThrough is null, it implies an open-ended period, so no expiration
    return DiscountStatus.ACTIVE;
  }

  getDiscountDetails(discountId: number): Observable<DiscountDetails> {
    throw new Error('Method not implemented.');
  }
}
